import { createAsyncThunk } from '@reduxjs/toolkit';
import QueryString from 'qs';
import { cloneDeep } from 'lodash-es';
import ApiaApi, { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import { arrayOrArray } from '../../../../../utils/util';
import store, { TRootState } from '../../../..';
import { TApiaFilter, TApiaFunction, TApiaLoad } from '../../../../../types';
import { TResponsiveTableRow } from '../../../../../components/common/generic/responsiveTable/types';
import { parseRows } from '../../util/rowsParser';
import { buttonsActions } from '../../../../buttonsSlice';
import { TListRowsStructure } from '../../../types';
import { listActions } from '../../..';
import notifyRequiredFilters from '../../util/notifyRequiredFilters';
import { notify } from '../../../../../components/common/notifications/util';
import { TApiaMetaFilter } from '../../../../documentsMonitorSlice/types';
import { ApiaUtil } from '@apia/components';

export type TTableRows = {
  rows: TResponsiveTableRow[];
};

const filter = createAsyncThunk<
  TListRowsStructure & {
    filters?: TApiaFilter[] | undefined;
    customLabel?: string;
  },
  {
    listId: string;
    filters?: TApiaFilter[];
    filtersDeleted?: boolean;
    forceAjaxUrl?: boolean;
    forceTabId?: boolean;
    freeMetadata?: TApiaMetaFilter[];
    metadata?: TApiaMetaFilter[];
  },
  { state: TRootState }
>(
  'listSlice/filter',
  async (
    {
      forceAjaxUrl = false,
      forceTabId = false,
      listId,
      filters,
      filtersDeleted,
      freeMetadata,
      metadata,
    },
    thunkApi,
  ) => {
    store.dispatch(
      buttonsActions.updateAction([
        {
          performing: true,
          action: 'disableTable',
        },
      ]),
    );

    const state = thunkApi.getState().listSlice.lists[listId];
    const { dispatch } = thunkApi;

    // Helper function to check if a value is empty (undefined or empty string)
    const isEmpty = (value: any) => value === undefined || value === '';

    /**
     * La siguiente logica se encarga de detecctar si todos los filtros
     * requeridos fueron ingresados. De ser asi o si no hay filtros requeridos,
     * isRequirementSet deberia cambiar a true
     */
    let isRequirementSet = false;
    let required = 0;
    let withValue = 0;

    if (filters) {
      state.filters?.forEach((innerFilter) => {
        if (
          !filters.find(
            (incommingfilter) => incommingfilter.id === innerFilter.id,
          )
        ) {
          if (innerFilter.required) {
            required++;
            if (['date', 'D'].includes(innerFilter.type as string)) {
              if (
                innerFilter.dateFromRequired &&
                !isEmpty(innerFilter.currentValue)
              ) {
                withValue++;
              } else if (
                innerFilter.dateRequired &&
                !isEmpty(innerFilter.currentValue) &&
                !isEmpty(innerFilter.filterToValue)
              ) {
                withValue++;
              } else if (
                innerFilter.dateToRequired &&
                !isEmpty(innerFilter.filterToValue)
              ) {
                withValue++;
              }
            } else if (innerFilter.currentValue) {
              withValue++;
            }
          }
        }
      });
      filters.forEach((innerFilter) => {
        if (innerFilter.required) {
          required++;
          if (['date', 'D'].includes(innerFilter.type as string)) {
            if (
              innerFilter.dateFromRequired &&
              !isEmpty(innerFilter.currentValue)
            ) {
              withValue++;
            } else if (
              innerFilter.dateRequired &&
              !isEmpty(innerFilter.currentValue) &&
              !isEmpty(innerFilter.filterToValue)
            ) {
              withValue++;
            } else if (
              innerFilter.dateToRequired &&
              !isEmpty(innerFilter.filterToValue)
            ) {
              withValue++;
            }
          } else if (innerFilter.currentValue) {
            withValue++;
          }
        }
      });
    }

    if (required === withValue) isRequirementSet = true;

    /**
     * La siguiente logica se encarga de guardar en newFilters cada filtro en
     * el estado con los cambios de valor realizados
     */

    const newFilters = filters
      ? (state.filters?.map((currentFilter) => {
          let updatingFilter: TApiaFilter | undefined = cloneDeep(
            filters.find((searchFilter) => {
              if (
                ['date', 'D'].includes(searchFilter.type ?? '') &&
                ['date', 'D'].includes(currentFilter.type ?? '')
              ) {
                if (searchFilter.id === currentFilter.filterToId) {
                  return searchFilter.id === currentFilter.filterToId;
                }
                return searchFilter.id === currentFilter.id;
              }

              return searchFilter.id === currentFilter.id;
            }),
          );

          if (updatingFilter) {
            if (
              ['date', 'D'].includes(currentFilter.type ?? '') &&
              updatingFilter.id === currentFilter.filterToId
            ) {
              updatingFilter.id = currentFilter.id;
              updatingFilter.filterToValue = updatingFilter.currentValue;
              updatingFilter.currentValue = currentFilter.currentValue;
              updatingFilter = {
                ...currentFilter,
                currentValue: updatingFilter.currentValue,
                filterToValue: updatingFilter.filterToValue,
                deleteFiltersTimestamp: updatingFilter.deleteFiltersTimestamp,
                changeFiltersTimestamp: updatingFilter.changeFiltersTimestamp,
                errorFrom:
                  !!updatingFilter.currentValue &&
                  currentFilter.errorFrom === 'Filtro requerido' &&
                  updatingFilter.errorFrom === undefined
                    ? undefined
                    : updatingFilter.errorFrom
                      ? updatingFilter.errorFrom
                      : !updatingFilter.currentValue &&
                          currentFilter.errorFrom === 'Filtro requerido'
                        ? currentFilter.errorFrom
                        : undefined,
                errorTo:
                  !!updatingFilter.filterToValue &&
                  currentFilter.errorTo === 'Filtro requerido' &&
                  updatingFilter.errorTo === undefined
                    ? undefined
                    : updatingFilter.errorTo
                      ? updatingFilter.errorTo
                      : !updatingFilter.filterToValue &&
                          currentFilter.errorTo === 'Filtro requerido'
                        ? currentFilter.errorTo
                        : undefined,
                error:
                  !!updatingFilter.filterToValue &&
                  !!updatingFilter.currentValue &&
                  currentFilter.error === 'Filtro requerido' &&
                  updatingFilter.error === undefined
                    ? undefined
                    : updatingFilter.error
                      ? updatingFilter.error
                      : !(
                            !!updatingFilter.filterToValue &&
                            !!updatingFilter.currentValue
                          ) && currentFilter.error === 'Filtro requerido'
                        ? currentFilter.error
                        : undefined,
              };
            } else if (
              updatingFilter.options &&
              !(
                [
                  'processesAdHocControl',
                  'processesAdHocControl',
                  'processFinalizeControl',
                  'processPriorityControl',
                  'processReasignControl',
                  'processesRollbackControl',
                  'processChangeDateControl',
                ].includes(window.listData[listId].component) &&
                updatingFilter.id === 'cmbSta'
              )
            ) {
              updatingFilter.options = updatingFilter.options.map((option) => {
                if (updatingFilter?.currentValue === option.value) {
                  return {
                    ...option,
                    selected: true,
                  };
                }
                return {
                  ...option,
                  selected: false,
                };
              });

              updatingFilter.error =
                !!updatingFilter.currentValue &&
                currentFilter.error === 'Filtro requerido'
                  ? undefined
                  : currentFilter.error;
            }

            return { ...currentFilter, ...updatingFilter };
          }

          return currentFilter;
        }) ?? [])
      : (state.filters ?? []);

    /**
     * La siguiente logica se encarga de armar el payload que se enviara para
     * settear los filtros en el bean
     */

    const payloadFilters: Record<string, unknown> = {};

    if (metadata) {
      payloadFilters.strDocMetadata = metadata
        .map((f) => {
          return `${f.id}${window.PRIMARY_SEPARATOR}${f.metaFilterType}${window.PRIMARY_SEPARATOR}${f.currentValue}`;
        })
        .join(';');
    }
    if (freeMetadata) {
      payloadFilters.strDocFreeMetadata = freeMetadata
        .map((f) => {
          return `${f.title}${window.PRIMARY_SEPARATOR}${f.currentValue}`;
        })
        .join(';');
    }

    newFilters?.forEach((currentFilter) => {
      if (currentFilter.isSize && currentFilter.sizeMultiplier) {
        payloadFilters[`${currentFilter.id}`] =
          currentFilter.currentValue === ''
            ? ''
            : ApiaUtil.instance.parsers.jsNumber(
                String(currentFilter.currentValue),
              ) * currentFilter.sizeMultiplier;
      } else {
        payloadFilters[`${currentFilter.id}`] = currentFilter.currentValue;
      }
      if (currentFilter.options) {
        currentFilter.options.forEach((option) => {
          if (option.selected) {
            payloadFilters[`${currentFilter.id}`] = option.value;
          }
        });
      }
      if (currentFilter.filterToId) {
        if (currentFilter.isSize && currentFilter.sizeMultiplier) {
          payloadFilters[`${currentFilter.filterToId as string}`] =
            currentFilter.filterToValue === ''
              ? ''
              : ApiaUtil.instance.parsers.jsNumber(
                  String(currentFilter.filterToValue),
                ) * currentFilter.sizeMultiplier;
        } else {
          payloadFilters[`${currentFilter.filterToId as string}`] =
            currentFilter.filterToValue;
        }
      }
    });

    if (isRequirementSet) {
      const result = await ApiaApi.post<TApiaLoad<TApiaFunction>>(
        makeApiaUrl({
          action: 'filter',
          preventAsXmlParameter: true,
          isAjax: true,
          timestamp: Date.now(),
          react: true,
          ajaxUrl: forceAjaxUrl ? state.ajaxUrl : undefined,
          tabId: forceTabId ? state.tabId : undefined,
        }),
        {
          postData: QueryString.stringify(payloadFilters),
        },
      );
      store.dispatch(
        buttonsActions.updateAction([
          {
            performing: false,
            action: 'disableTable',
          },
        ]),
      );
      if (result && result.data) {
        const newRows = arrayOrArray(
          result.data.function.messages.result.table?.row,
        );
        const rowIds = new Set();
        let ignoreIndex: number | null = null;
        newRows.forEach((row, i) => {
          if (rowIds.has(row.id)) {
            ignoreIndex = i;
          } else {
            rowIds.add(row.id);
          }
        });
        const parsedRows = newRows.filter((_, i) => {
          return i !== ignoreIndex;
        });
        return {
          rows: parseRows(listId, arrayOrArray(parsedRows)),
          pageInfo: result.data.function.messages.result.pageInfo,
          filters: notifyRequiredFilters(listId, newFilters),
          customLabel: filtersDeleted ? window.MSG_FIRST_TIME : '',
        };
      }
    } else {
      store.dispatch(
        buttonsActions.updateAction([
          {
            performing: false,
            action: 'disableTable',
          },
        ]),
      );
      const notifications = document.querySelectorAll(
        '.notificationsFloatingList .notification__text',
      );
      if (notifications.length > 0) {
        notifications.forEach((current) => {
          if (current.textContent !== window.MSG_FILTER_REQUIRED) {
            notify({
              message: window.MSG_FILTER_REQUIRED,
              type: 'warning',
            });
          }
        });
      } else {
        notify({
          message: window.MSG_FILTER_REQUIRED,
          type: 'warning',
        });
      }
      void dispatch(
        listActions.setState({
          listId,
          rows: [],
          filters: notifyRequiredFilters(listId, newFilters),
          customLabel: window.MSG_FILTER_REQUIRED,
        }),
      );
      return {
        rows: [],
        filters: notifyRequiredFilters(listId, newFilters),
        customLabel: window.MSG_FILTER_REQUIRED,
      };
    }
    throw new Error();
  },
);

export default filter;
